import {PeoClientApiModel} from "../model/peo-client-api.model";

/**
 * @class
 * @description This class provides methods to interact with the Admin API.
 * @export
 * @name UtilityService
 * @service
 */
export class UtilityService {

  /**
   * @description This property contains an empty string.
   * @memberof UtilityService
   * @property
   * @name UtilityService.EmptyString
   * @public
   * @static
   * @type {string}
   */
  public static EmptyString: string = '';

  /**
   * @description This method returns the current date in UTC format.
   * @memberof UtilityService
   * @method
   * @name UtilityService.DateTimeUtc()
   * @param {Date?} [date] The date to convert to UTC format.
   * @public
   * @returns {Date} The date in UTC format.
   * @static
   */
  public static DateTimeUtc(date?: Date): Date {

    // If a date is provided, use it. Otherwise, use the current date.
    const timestamp: Date = date ?? new Date();

    // Return the date in UTC format.
    return new Date(Date.UTC(timestamp.getUTCFullYear(), timestamp.getUTCMonth(), timestamp.getUTCDate(),
      timestamp.getUTCHours(), timestamp.getUTCMinutes(), timestamp.getUTCSeconds(), timestamp.getUTCMilliseconds()));
  }

  /**
   * @description This method returns the duplicates in an array of objects based on the specified properties.
   * @memberof UtilityService
   * @method
   * @name UtilityService.GetDuplicates()
   * @param {T[]} objects The array of objects to check for duplicates.
   * @param {...(keyof T)[]} properties The properties to check for duplicates.
   * @public
   * @returns {T[]} The duplicates in the array of objects.
   * @static
   * @template T The type of object in the objects array.
   */
  public static GetDuplicates<T>(objects: T[], ...properties: (keyof T)[]): T[] {

    // Define our response results.
    const result: T[] = [];

    // Define our set of seen values.
    const seen = new Set<string>();

    // Iterate over the objects.
    for (const o of objects) {

      // Define our key parts
      const keyParts: string[] = [];

      // Iterate over the properties and append them to the key.
      for (const p of properties) keyParts.push(o[p] as string);

      // Define our full key.
      const key: string = keyParts.join('`');

      // If we have seen the key, add all objects matching the key to the result.
      if (seen.has(key)) result.push(...objects.filter((o: T): boolean =>
        properties.map((p: keyof T): string => o[p] as string).join('`') === key));

      // Otherwise, add the key to the set.
      else seen.add(key);
    }

    // We're done, return the result.
    return result;
  }

  /**
   * @description This method returns the unique objects in an array of objects based on the specified properties.
   * @memberof UtilityService
   * @method
   * @name UtilityService.GetUnique()
   * @param {T[]} objects The array of objects to check for unique objects.
   * @param {...(keyof T)[]} properties The properties to check for unique objects.
   * @public
   * @returns {T[]} The unique objects in the array of objects.
   * @static
   * @template T The type of object in the objects array.
   */
  public static GetUnique<T>(objects: T[], ...properties: (keyof T)[]): T[] {

    // Define our response results.
    const result: T[] = [];

    // Define our set of seen values.
    const seen = new Set<string>();

    // Iterate over the objects.
    for (const o of objects) {

      // Define our key parts
      const keyParts: string[] = [];

      // Iterate over the properties and append them to the key.
      for (const p of properties) keyParts.push(o[p] as string);

      // Define our full key.
      const key: string = keyParts.join('`');

      // If we have seen the key, add it to the result.
      if (!seen.has(key)) {

        // Add the object to the result.
        result.push(o);

        // Add the key to the set.
        seen.add(key);
      }
    }

    // Clear the set.
    seen.clear();

    // We're done, return the result.
    return result;
  }

  /**
   * @description This method determines whether a string value is empty or not.
   * @memberof UtilityService
   * @method
   * @name UtilityService.IsEmptyString()
   * @param {string?} value The string value to check.
   * @public
   * @returns {boolean} True if the string value is empty; otherwise, false.
   * @static
   */
  public static IsEmptyString(value: string | null | undefined): boolean {

    // Check for an empty string.
    if (value === null || value === undefined || value.trim() === this.EmptyString) return true;

    // The string is not empty.
    return false;
  }

  /**
   * @description This method returns a multi-column filter predicate for a table.
   * @memberof UtilityService
   * @method
   * @name UtilityService.TableMultiFilterPredicate()
   * @param {(keyof T)[]} columns The columns to filter.
   * @returns {(data: T, filter: string) => boolean} The filter predicate.
   * @static
   * @template T The type of data to filter.
   */
  public static TableMultiFilterPredicate<T>(columns: (keyof T)[]):
    ((data: T, filter: string) => boolean) {

    // Return the filter predicate.
    return (data: T, filter: string): boolean => {

      // Localize our filter value object.
      const filterValueObject: { [key: string]: string } = JSON.parse(filter);

      // Define our matches flag.
      let matches: boolean = false;

      // Iterate over the filter columns.
      columns.forEach((c: keyof T): void => {

        // Check for a match.
        if (!!filterValueObject[c as string]) {

          // Localize the filter value.
          const filterValue: string = filterValueObject[c as string].trim().toLowerCase();

          // Localize the data value.
          const dataValue: string | undefined = data[c as keyof T]?.toString().trim().toLowerCase();

          // Check for a match.
          if ((dataValue === filterValue) || (dataValue?.includes(filterValue) ?? false)) {

            // Set the matches flag.
            matches = true;

            // We're done, so break out of the loop.
            return;
          }
        }
      });

      // Return the matches flag.
      return matches;
    };
  }
}
